/** Runtime-only microphone recording drafts keyed by Canvas node id (never persisted to canvas JSON). */

import type { VoiceReferenceQualityAnalysis } from './voiceReferenceQualityPolicy'
import type { VoiceRecordingPhase } from './useVoiceMicrophoneRecording'

export type VoiceReferenceRecordingDraftStatus =
  | 'idle'
  | 'recording'
  | 'recorded_local'
  | 'analyzing'
  | 'ready_local'
  | 'uploading'
  | 'ready_remote'
  | 'upload_failed'
  | 'error'

export type VoiceReferenceRecordingDraft = {
  nodeId: string
  status: VoiceReferenceRecordingDraftStatus
  phase: VoiceRecordingPhase
  blob: Blob | null
  objectUrl: string | null
  mimeType: string | null
  durationMs: number
  createdAt: number | null
  quality: VoiceReferenceQualityAnalysis | null
  uploadErrorKey: string | null
  uploadErrorDetail: string | null
}

const drafts = new Map<string, VoiceReferenceRecordingDraft>()

const BLOB_RETAINING_STATUSES: ReadonlySet<VoiceReferenceRecordingDraftStatus> = new Set([
  'recorded_local',
  'analyzing',
  'ready_local',
  'uploading',
  'upload_failed',
])

function emptyDraft(nodeId: string): VoiceReferenceRecordingDraft {
  return {
    nodeId,
    status: 'idle',
    phase: 'idle',
    blob: null,
    objectUrl: null,
    mimeType: null,
    durationMs: 0,
    createdAt: null,
    quality: null,
    uploadErrorKey: null,
    uploadErrorDetail: null,
  }
}

export function getVoiceReferenceRecordingDraft(nodeId: string): VoiceReferenceRecordingDraft | null {
  return drafts.get(nodeId) ?? null
}

export function upsertVoiceReferenceRecordingDraft(
  nodeId: string,
  patch: Partial<Omit<VoiceReferenceRecordingDraft, 'nodeId'>>,
): VoiceReferenceRecordingDraft {
  const prev = drafts.get(nodeId) ?? emptyDraft(nodeId)
  const next: VoiceReferenceRecordingDraft = { ...prev, ...patch, nodeId }
  drafts.set(nodeId, next)
  return next
}

export function clearVoiceReferenceRecordingDraft(nodeId: string): void {
  const prev = drafts.get(nodeId)
  if (prev?.objectUrl) {
    URL.revokeObjectURL(prev.objectUrl)
  }
  drafts.delete(nodeId)
}

export function clearVoiceReferenceRecordingDrafts(nodeIds: string[]): void {
  for (const nodeId of nodeIds) clearVoiceReferenceRecordingDraft(nodeId)
}

/** True when a local Blob still exists and can be retried / previewed. */
export function hasVoiceReferenceRecordingDraft(nodeId: string): boolean {
  const draft = drafts.get(nodeId)
  return Boolean(draft?.blob && BLOB_RETAINING_STATUSES.has(draft.status))
}

export function isVoiceReferenceUploadInFlight(nodeId: string): boolean {
  return getVoiceReferenceRecordingDraft(nodeId)?.status === 'uploading'
}

export function isVoiceReferenceUploadFailed(nodeId: string): boolean {
  return getVoiceReferenceRecordingDraft(nodeId)?.status === 'upload_failed'
}
